import clsx from "clsx";
import deburr from "lodash/deburr";
import React from "react";
import Highlighter from "react-highlight-words";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import environment from "web/script/modules/environment";
import { StandardFilterProps } from "web/types/feed-filters";
import { CategoryFilterOption, InputFilterOption } from "web/types/universal-filters";
import * as styles from "./checkbox-filter-item.css";

interface CheckboxFilterItemProps extends StandardFilterProps {
    item: InputFilterOption | CategoryFilterOption;
    isSmallItem?: boolean;
    hasChildren?: boolean;
    ariaControls?: string;
    siblingOpen?: boolean;
    query?: string;
}

export default function CheckboxFilterItem({
    query,
    ariaControls,
    isSmallItem = false,
    item,
    hasChildren,
    siblingOpen,
    pendingFilterInfo,
    ...props
}: CheckboxFilterItemProps): JSX.Element {
    const isCategoryHeading = item.type === "category";
    const isRadio = isCategoryHeading || item.type === "radio";
    const isChecked = pendingFilterInfo.getChecked(item);

    function onClick(event: React.MouseEvent<HTMLAnchorElement>): void {
        event.preventDefault();
        props.onClick(item);
    }

    function onKeyDown(event: React.KeyboardEvent<HTMLAnchorElement>): void {
        if (event.key === " " || event.key === "Spacebar") {
            props.onClick(item);
            event.preventDefault();
        }
    }

    return (
        <a
            role={isRadio ? "radio" : "checkbox"}
            tabIndex={0}
            aria-checked={isChecked}
            href={item.url}
            className={clsx(styles.filterItem, {
                [styles.selectedLabel]: isChecked,
                [styles.smallFilterItem]: isSmallItem,
                [styles.siblingOpen]: siblingOpen && !isChecked,
            })}
            onClick={onClick}
            onKeyDown={onKeyDown}
            aria-expanded={hasChildren ? isChecked : undefined}
            aria-controls={hasChildren ? ariaControls : undefined}
        >
            <div
                className={clsx(styles.icon, {
                    [styles.iconChecked]: isChecked,
                })}
            >
                {isRadio && isChecked ? <SVGIcon name="round-radiobutton-checked" /> : null}
                {isRadio && !isChecked ? <SVGIcon name="round-radiobutton" /> : null}
                {!isRadio && isChecked ? <SVGIcon name="round-checkbox-checked" /> : null}
                {!isRadio && !isChecked ? <SVGIcon name="round-checkbox" /> : null}
            </div>
            <label
                htmlFor={item.id ? `${item.id}-control` : undefined}
                className={styles.labelItem}
            >
                {query ? (
                    <Highlighter
                        highlightClassName={styles.highlighted}
                        searchWords={[query]}
                        textToHighlight={item.label}
                        autoEscape={true}
                        sanitize={deburr}
                    />
                ) : (
                    item.label
                )}
            </label>
            {hasChildren &&
                isRadio &&
                !(
                    environment.getFeature("seo_enable_relevant_feed_filters") && isCategoryHeading
                ) && (
                    <SVGIcon
                        className={clsx(styles.arrow, { [styles.rotate]: isChecked })}
                        name="square-chevron-thin"
                    />
                )}
        </a>
    );
}
